import { api_fetch } from "@/api/client"
import { DeductionExt } from "@/api/deduction"
import { Badge, BadgeVariantOptionType } from "@/components/ui/badge"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { toast } from "@/components/ui/use-toast.ts"
import { CATEGORY_COLORS } from "@/deduction/CategoryBreakdown.tsx"
import { classes } from "@/utils"
import { capitalCase } from "change-case"
import { useState } from "preact/hooks"
import { z } from "zod"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { CATEGORY_FORM_SCHEMA, CategoryForm } from "./CategoryForm"

interface CategoryCellProps {
  /** Only what the editor reads, so lists that aren't the deductions table can reuse it. */
  deduction: Pick<DeductionExt, "id" | "category" | "category_description" | "kind">
  className?: string
  /** Called after a successful save, for pages that don't reload off `RefreshFlag`. */
  onSaved?: () => void
}

const _COLORS = [
  "bg-amber-100",
  "text-amber-700",
  "hover:bg-amber-200",
  "bg-emerald-100",
  "text-emerald-700",
  "hover:bg-emerald-200",
  "bg-fuchsia-100",
  "text-fuchsia-700",
  "hover:bg-fuchsia-200",
  "bg-sky-100",
  "text-sky-700",
  "hover:bg-sky-200",
  "bg-rose-100",
  "text-rose-700",
  "hover:bg-rose-200",
  "bg-slate-100",
  "text-slate-700",
  "hover:bg-slate-200",
  "bg-teal-100",
  "text-teal-700",
  "hover:bg-teal-200",
  "bg-lime-100",
  "text-lime-700",
  "hover:bg-lime-200",
  "bg-indigo-100",
  "text-indigo-700",
  "hover:bg-indigo-200",
  "bg-emerald-100",
  "text-emerald-700",
  "hover:bg-emerald-200",
  "bg-lime-100",
  "text-lime-700",
  "hover:bg-lime-200",
]

export function CategoryCell({ deduction, className, onSaved }: CategoryCellProps) {
  const [categoryPopoverOpen, setCategoryPopoverOpen] = useState(false)
  const value = deduction.category
  const color = value ? CATEGORY_COLORS[value] : "gray"

  async function handleSubmit(values: z.infer<typeof CATEGORY_FORM_SCHEMA>) {
    const res = await api_fetch(`/deduction/${deduction.id}/category`, {
      method: "POST",
      body: values,
    })

    if (!res.ok) {
      console.error("Failed to update category", res.error)
      toast({
        title: "Failed to update category",
        description: res.error.message,
        variant: "destructive",
      })
      return false
    }

    setCategoryPopoverOpen(false)
    RefreshFlag.set(flag => !flag)
    onSaved?.()
    return true
  }

  const displayValue = value ? capitalCase(value) : "Misc"

  return (
    <Popover modal={true} open={categoryPopoverOpen} onOpenChange={setCategoryPopoverOpen}>
      <PopoverTrigger
        onClick={(e: MouseEvent) => {
          e.preventDefault()
          e.stopImmediatePropagation()
          if (deduction.kind !== "deduction" && deduction.kind !== "repayment") {
            return
          }
          setCategoryPopoverOpen(!categoryPopoverOpen)
        }}>
        <Badge
          variant={color as BadgeVariantOptionType}
          // override the hover effect for Early Pay rows b/c they're not meant to be interactive
          className={classes(
            "text-center cursor-pointer text-nowrap",
            className,
            deduction.kind === "invoice" && "hover:bg-amber-500"
          )}>
          {displayValue}
        </Badge>
      </PopoverTrigger>
      <PopoverContent collisionPadding={200} className="text-plue-900">
        <CategoryForm
          initialValue={value}
          initialDescription={deduction.category_description}
          deductionId={deduction.id}
          onSubmit={handleSubmit}
        />
      </PopoverContent>
    </Popover>
  )
}
